
import React from 'react';
import { CheckCircle2, Home, Banknote } from 'lucide-react';
import Layout from '../components/Layout';
import GallerySection from '../components/GallerySection';
import { SERVICES, ASSETS } from '../data';

const NytTagPage = () => {
  const service = SERVICES.find(s => s.id === 'nyt-tag');

  return (
    <Layout>
      {/* Hero */}
      <section className="relative pt-56 pb-24 flex items-center justify-center bg-slate-900">
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src={service.image} 
            alt={service.title} 
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
        </div>
        <div className="relative z-10 text-center">
          <h1 
            className="text-[1rem] md:text-7xl font-bold text-white tracking-tighter"
          >
            {service.title.toUpperCase()}
          </h1>
          <p className="text-amber-500 font-bold tracking-[0.3em] uppercase mt-4 px-[15px]">Kvalitet fra top til bund i Aalborg & Omegn</p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6 leading-tight uppercase tracking-tight">
                NYT TAG I AALBORG - <span className="text-amber-500">FÅ EN PRIS</span>
              </h2>
              <div className="space-y-6 text-slate-600 text-lg font-light leading-relaxed">
                <p>
                  Hos Jensen & Sørensen ApS tilbyder vi at lægge nyt tag i Aalborg, Nørresundby, Brønderslev og Støvring området. Vi har erfaring med forskellige typer af tag, herunder tegltag, betonstentag, tagpap og eternittag. Vi vejleder dig gerne til at vælge hvilket type af tag, der passer bedst til din bolig og dine ønsker.
                </p>
                <p>
                  Vi sørger for at finde den bedste løsning sammen med dig, og sørger for at dit nye tag bliver lagt præcist og holdbart.
                </p>
              </div>
              
              <div className="mt-10 p-8 bg-slate-50 border-l-4 border-amber-500">
                <p className="text-slate-900 font-medium text-xl">
                  Kontakt os i dag og få et uforpligtende tilbud på dit nye tag.
                </p>
                <a href="/kontakt" className="inline-block mt-4 text-amber-500 font-bold hover:text-amber-600 transition-colors uppercase tracking-wider">Kontakt os nu →</a>
              </div>
            </div>

            <div className="relative bg-slate-900 overflow-hidden shadow-2xl rounded-xl">
              <video 
                className="w-full h-auto"
                poster={service.image}
                controls
                playsInline
              >
                <source src={service.video} type="video/mp4" />
                Din browser understøtter ikke video-tagget.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Content Section */}
      <section className="py-24 bg-slate-900 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-amber-500/5 -skew-x-12 translate-x-1/2" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="w-full">
                <img src="https://assets.auxodev.dk/assets/650c050a-1b3b-43e1-83de-064d04c7258f/8d802620-0d19-4ea4-a715-5d75fa198e25.jpeg" alt="Tagarbejde" className="w-full h-auto rounded-2xl shadow-2xl object-cover" />
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-3xl font-bold uppercase tracking-tight">NYT TAG TIL ENHVER BOLIG</h2>
              </div>
              <div className="space-y-6 text-slate-300 text-lg font-light leading-relaxed">
                <p>
                  Vi har erfaring med at lægge nyt tag på alle typer huse, lige fra 70’er huse til moderne sommerhuse. Et nyt tag kan give din bolig et frisk og moderne udseende, samtidig med at det forbedrer isoleringen og beskytter dit hjem mod vejr og vind.
                </p>
                <p>
                  I en tid hvor bæredygtighed er i fokus, tilbyder vi også løsninger som nyt tag med solceller og tegltag eller tagpap. Et nyt tag med solceller kan hjælpe med at reducere din elregning og gøre din bolig mere effektiv.
                </p>
                <p>
                  Et nyt tag med tagpap er en populær og holdbar løsning, der kræver minimal vedligeholdelse. Vi hjælper dig med at vælge den bedste løsning til din bolig og sikrer, at montering af taget bliver udført korrekt og professionelt.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Renovation & Price Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Renovering */}
            <div className="bg-slate-50 p-10 rounded-2xl border border-slate-100">
              <h3 className="text-2xl font-bold text-slate-900 mb-4 uppercase tracking-tight">RENOVERING AF TAG - EN ØKONOMISK LØSNING</h3>
              <p className="text-slate-600 leading-relaxed font-light mb-6">
                Hvis dit tag er for godt til at blive helt udskiftet, kan vi hos Jensen & Sørensen ApS også hjælpe med tagrenovering. Vi kommer gerne forbi og tjekker dit tag, undertag og konstruktion for at danne os et overblik over, hvor meget af taget der eventuelt skal udskiftes.
              </p>
              <p className="text-slate-600 leading-relaxed font-light">
                En renovering kan være en økonomisk god løsning, der forlænger tagets levetid og forbedrer funktionalitet uden behov for en fuldstændig udskiftning.
              </p>
            </div>

            {/* Pris */}
            <div className="bg-slate-900 p-10 rounded-2xl text-white shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Banknote size={120} />
              </div>
              <h3 className="text-2xl font-bold mb-4 uppercase tracking-tight">HVAD KOSTER ET NYT TAG?</h3>
              <p className="text-slate-300 leading-relaxed font-light mb-6">
                Prisen på et nyt tag afhænger af flere forskellige ting, som hvor stort er taget og huset, samt hvilken tagtype der skal lægges. Derfor varierer prisen på nyt tag efter materialevalg og om hvor omfattende opgaven er.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="text-amber-500" size={20} />
                  <span className="font-medium">Konkurrencedygtige priser</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="text-amber-500" size={20} />
                  <span className="font-medium">Skræddersyede tilbud</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 className="text-amber-500" size={20} />
                  <span className="font-medium">Hurtig besvarelse</span>
                </div>
              </div>
              <div className="mt-8">
                <p className="text-amber-500 font-bold text-lg">Ring til os i dag:</p>
                <div className="flex gap-4 mt-2">
                  <a href="tel:28398610" className="hover:text-amber-500 transition-colors">28 39 86 10</a>
                  <span className="text-slate-700">|</span>
                  <a href="tel:24343920" className="hover:text-amber-500 transition-colors">24 34 39 20</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <GallerySection images={ASSETS.nytTagGallery} title="Galleri - Nyt tag" />

      {/* Final CTA */}
      <section className="py-20 bg-amber-500">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 uppercase tracking-tighter">
            Skal vi kigge på dit tag?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/kontakt" 
              className="px-10 py-4 bg-slate-900 text-white font-bold rounded-full hover:bg-slate-800 transition-all uppercase tracking-widest text-sm"
            >
              Få et uforpligtende tilbud
            </a>
            <a 
              href="mailto:jsbyg.info@gmail.com" 
              className="px-10 py-4 bg-white text-slate-900 font-bold rounded-full hover:bg-slate-50 transition-all uppercase tracking-widest text-sm border border-slate-200"
            >
              Send os en mail
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NytTagPage;
